import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import DiseaseApi from 'api/DiseaseApi';
import LocationApi from 'api/LocationApi';
import UserApi from 'api/UserApi';
import LogApi from 'api/LogApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';
import { initialize } from 'utils/analytics';

import { fetchDiseases } from 'diseases/diseases.saga';
import { loadAvailableDiseasesSuccess, loadDiseaseGroupsSuccess } from 'diseases/diseases.slice';
import { loadAllLocationsSuccess } from 'locations/locations.slice';

import {
  finishLoadingSettings,
  finishSubmittingSettings,
  setGroups,
  setRoles,
  setUserProfile,
  startLoadingSettings,
  startSubmittingSettings,
} from './user.slice';

export const fetchUserProfile = createAction('user/fetchProfile');
export const fetchAllUserTypes = createAction('user/fetchAllType');
export const fetchAllUserGroups = createAction('user/fetchAllGroups');
export const fetchCustomSettingsInfo = createAction('user/fetchCustomSettingsInfo');
export const updateUserProfile = createAction<dto.UserPersonalDetailsModel>('user/updateProfiles');
export const updateUserNotifications = createAction<dto.UserNotificationsModel>(
  'user/updateNotifications'
);
export const updateCustomSettings = createAction<dto.UserCustomSettingsModel>(
  'user/updateCustomSettings'
);

export function* fetchUserProfileSaga() {
  try {
    const { data }: AxiosResponse<dto.UserModel> = yield call(UserApi.getProfile);

    yield put(setUserProfile(data));

    if (!data.isDoNotTrack) initialize({ userId: data.id, groupId: data.groupId });
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
  }
}

export function* fetchAllUserTypesSaga() {
  try {
    const { data }: AxiosResponse<dto.UserTypeModel[]> = yield call(UserApi.getRoles);
    yield put(setRoles(data));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
  }
}

export function* fetchAllUserGroupsSaga() {
  try {
    const { data }: AxiosResponse<dto.UserGroupModel[]> = yield call(UserApi.getGroups);
    yield put(setGroups(data));
  } catch (e) {
    // This will 403 on any user that isn't admin, so silencing that will stop logging spam
    if (e?.response?.status !== 403) {
      LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
      console.error(JSON.stringify(e));
    }
  }
}

export function* updateUserProfileSaga(action: PayloadAction<dto.UserPersonalDetailsModel>) {
  try {
    yield put(startSubmittingSettings());

    const { data }: AxiosResponse<dto.UserModel> = yield call(
      UserApi.updateProfile,
      action.payload
    );
    yield put(setUserProfile(data));
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Your user settings have been updated',
      })
    );
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error updating user settings' })
    );
  } finally {
    yield put(finishSubmittingSettings());
  }
}

export function* updateUserNotificationsSaga(action: PayloadAction<dto.UserNotificationsModel>) {
  try {
    yield put(startSubmittingSettings());

    const { data }: AxiosResponse<dto.UserModel> = yield call(
      UserApi.updateNotificationSettings,
      action.payload
    );
    yield put(setUserProfile(data));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error updating notificatoin settings',
      })
    );
  } finally {
    yield put(finishSubmittingSettings());
  }
}

export function* updateCustomSettingsSaga(action: PayloadAction<dto.UserCustomSettingsModel>) {
  try {
    yield put(startSubmittingSettings());
    const { data }: AxiosResponse<dto.UserModel> = yield call(
      UserApi.updateCustomSettings,
      action.payload
    );

    yield put(setUserProfile(data));

    const locations: dto.GetUserLocationModel = yield call(LocationApi.getUserLocations);

    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Your Insights settings have been updated',
      })
    );

    // Update risk matrix
    yield put(loadAllLocationsSuccess(locations.geonames));
    yield put(fetchDiseases());
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error updating Insights settings',
      })
    );
  } finally {
    yield put(finishSubmittingSettings());
  }
}

export function* fetchCustomSettingsInfoSaga() {
  try {
    yield put(startLoadingSettings());

    const res: [dto.GetUserLocationModel, dto.DiseaseInformationModel[], dto.DiseaseGroupModel[]] =
      yield all([
        call(LocationApi.getUserLocations),
        call(DiseaseApi.getAllDiseases),
        call(DiseaseApi.getDiseaseGroups),
      ]);

    yield put(loadAllLocationsSuccess(res[0].geonames));
    yield put(loadAvailableDiseasesSuccess(res[1]));
    yield put(loadDiseaseGroupsSuccess(res[2]));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error fetching Insights settings',
      })
    );
  } finally {
    yield put(finishLoadingSettings());
  }
}

function* userSaga() {
  yield takeLatest(fetchUserProfile, fetchUserProfileSaga);
  yield takeLatest(fetchAllUserTypes, fetchAllUserTypesSaga);
  yield takeLatest(fetchAllUserGroups, fetchAllUserGroupsSaga);
  yield takeLatest(fetchCustomSettingsInfo, fetchCustomSettingsInfoSaga);
  yield takeLatest(updateUserProfile, updateUserProfileSaga);
  yield takeLatest(updateCustomSettings, updateCustomSettingsSaga);
  yield takeLatest(updateUserNotifications, updateUserNotificationsSaga);
}

export default userSaga;
